import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import '@/App.css';
import { AppProvider, useApp } from '@/context/AppContext';
import { Toaster } from '@/components/ui/sonner';
import AuthScreen from '@/components/AuthScreen';
import MapScreen from '@/components/MapScreen';
import CrewScreen from '@/components/CrewScreen';
import TripScreen from '@/components/TripScreen';
import ReportsScreen from '@/components/ReportsScreen';
import SettingsScreen from '@/components/SettingsScreen';
import AdminPanel from '@/components/AdminPanel';
import SharePage from '@/components/SharePage';
import FloatPlanScreen from '@/components/FloatPlanScreen';
import FloatPlanSharePage from '@/components/FloatPlanSharePage';
import ResetPasswordPage from '@/components/ResetPasswordPage';
import PrivacyPolicy from '@/components/PrivacyPolicy';
import TermsOfService from '@/components/TermsOfService';
import InstallPrompt from '@/components/InstallPrompt';
import PromoRedeemPage from '@/components/PromoRedeemPage';
import { Map, Users, Navigation, AlertTriangle, User, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const MainApp = () => {
  const { isAuthenticated, authLoading, unreadAlerts, setMapCenter, setFocusLocation } = useApp();
  const [activeTab, setActiveTab] = useState('map');
  const [reportPinPosition, setReportPinPosition] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-cyan-200">Loading Boat 360...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthScreen />;
  }

  // Show Admin Panel if requested
  if (showAdminPanel) {
    return <AdminPanel onClose={() => setShowAdminPanel(false)} />;
  }

  const handleViewFriendOnMap = (friend) => {
    if (friend.lat && friend.lon) {
      setMapCenter([friend.lat, friend.lon]);
      setFocusLocation({ lat: friend.lat, lon: friend.lon, nonce: Date.now() });
      setActiveTab('map');
    }
  };

  const handleReportClick = (pinPosition) => {
    setReportPinPosition(pinPosition || null);
    setActiveTab('reports');
  };

  const tabs = [
    { id: 'map', label: 'Map', icon: Map },
    { id: 'crew', label: 'Crew', icon: Users },
    { id: 'plan', label: 'Plan', icon: FileText },
    { id: 'trip', label: 'Trip', icon: Navigation },
    { id: 'reports', label: 'Reports', icon: AlertTriangle },
    { id: 'settings', label: 'Me', icon: User },
  ];

  return (
    <div className="app-container flex flex-col bg-gray-100" style={{ height: '100dvh', minHeight: '-webkit-fill-available' }}>
      {/* Main Content */}
      <main className="flex-1 overflow-hidden min-h-0">
        {activeTab === 'map' && <MapScreen onReportClick={handleReportClick} />}
        {activeTab === 'crew' && <CrewScreen onViewOnMap={handleViewFriendOnMap} />}
        {activeTab === 'plan' && <FloatPlanScreen />}
        {activeTab === 'trip' && <TripScreen />}
        {activeTab === 'reports' && <ReportsScreen initialPinPosition={reportPinPosition} onPinUsed={() => setReportPinPosition(null)} />}
        {activeTab === 'settings' && <SettingsScreen onOpenAdmin={() => setShowAdminPanel(true)} />}
      </main>

      {/* Bottom Navigation - Always Visible */}
      <nav 
        className="bg-white border-t shadow-lg flex-shrink-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around h-14">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                onTouchEnd={(e) => { e.preventDefault(); setActiveTab(tab.id); }}
                className={`flex flex-col items-center justify-center flex-1 h-full relative cursor-pointer ${
                  isActive ? 'text-cyan-600' : 'text-gray-400'
                }`}
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                data-testid={`nav-${tab.id}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-cyan-600' : 'text-gray-400'}`} />
                <span className={`text-[10px] mt-0.5 ${isActive ? 'font-medium' : ''}`}>
                  {tab.label}
                </span>
                {tab.id === 'crew' && unreadAlerts > 0 && (
                  <Badge className="absolute top-0 right-1/4 bg-red-500 text-white text-[9px] px-1 py-0 min-w-[16px] h-4">
                    {unreadAlerts}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public pages - no auth required */}
        <Route path="/share/:token" element={<SharePage />} />
        <Route path="/share/plan/:token" element={<FloatPlanSharePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/redeem" element={<PromoRedeemPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        
        {/* Main app with auth */}
        <Route path="/*" element={
          <AppProvider>
            <MainApp />
            <Toaster position="top-center" />
            <InstallPrompt />
          </AppProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
